import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useState } from 'react';

import { DeductiblesProvider } from 'modules';
import { useFieldDefinitions } from 'pages/QuoteBind/hooks/useFieldDefinitions';

import { RISK_ENDORSEMENT_FIELDS } from 'consts/quote-bind';
import { usePatchEndorsement, usePatchRisk, usePostDraftRisk, usePostEndorsement, usePostRisk } from 'lib/quoteBind';
// app
import * as utils from 'utils';

import { RiskContext } from './AddEditQuoteBind.context';
import MultiStepForm from './MultiStepForm';
import { MultiStepLoading } from './MultiStepLoading';

const AddEditQuoteBind = ({
  fullScreen,
  product,
  riskData,
  draftId,
  riskId,
  effectiveDate = null,
  duplicate = false,
  addEndorsement = false,
  renewal = false,
  reQuoteRenewal = false,
  endorsementId,
}) => {
  const type = useMemo(() => product?.value, []);
  const { mutateAsync: postRisk } = usePostRisk();
  const { mutateAsync: patchRiskData } = usePatchRisk();
  const { mutateAsync: postRiskDraft } = usePostDraftRisk();
  const { mutateAsync: postEndorsement } = usePostEndorsement();
  const { mutateAsync: patchEndorsement } = usePatchEndorsement();

  const [isLoading, setIsLoading] = useState(true);

  const isReQuote = (Boolean(riskId) && !duplicate && !renewal) || reQuoteRenewal;
  const isEndorsement = Boolean(riskId) && (addEndorsement || endorsementId);
  const isRenewal = Boolean(riskId) && renewal;
  const {
    definitionsFields,
    isLoading: isRiskdefinitionLoading,
    riskFieldOptions,
  } = useFieldDefinitions({ productCode: type, isEndorsement });

  const riskDefinitionsFields = (() => {
    if (isEndorsement) {
      const endorsementDefinitionFields = [...definitionsFields, ...RISK_ENDORSEMENT_FIELDS];
      const definition = utils.risk.disableFields(['inceptionDate', 'expiryDate'], endorsementDefinitionFields);
      const endorsementFields = utils.risk.disableAfterBind(definition);
      return utils.risk.disableAfterQuote(endorsementFields);
    }
    if (isRenewal) {
      const renewalFields = utils.risk.disableFields(['inceptionDate', 'expiryDate', 'newRenewal'], definitionsFields);
      return utils.risk.disableAfterQuote(renewalFields);
    }
    if (isReQuote) {
      return utils.risk.disableAfterQuote(definitionsFields || []);
    }
    return definitionsFields || [];
  })();

  useEffect(() => {
    let isSubscribed = true;

    const isLoadingValue = isRiskdefinitionLoading;
    isSubscribed && setIsLoading(isLoadingValue);

    return () => (isSubscribed = false);
  }, [isRiskdefinitionLoading]);

  const handlePostRisk = (values) => {
    if (isEndorsement) {
      return endorsementId
        ? patchEndorsement({ definitions: riskDefinitionsFields, values, riskId, riskData, effectiveDate, endorsementId })
        : postEndorsement({ definitions: riskDefinitionsFields, values, riskId, riskData, effectiveDate });
    }
    return isReQuote
      ? patchRiskData({ values, type, definitions: riskDefinitionsFields, riskId, riskData })
      : postRisk({ values: { ...values, ...(isRenewal && { renewedRiskId: riskId }) }, type, definitions: riskDefinitionsFields, draftId });
  };

  const handlePostDraftRisk = (values) => {
    draftId
      ? postRiskDraft({ values, type, definitions: riskDefinitionsFields, draftId })
      : postRiskDraft({ values, type, definitions: riskDefinitionsFields });
  };

  const defaultValues = riskData
    ? utils.form.getFormattedValues(riskData, riskDefinitionsFields)
    : utils.form.getInitialValues(riskDefinitionsFields);

  const riskProviderValue = useMemo(
    () => ({
      risk: { ...riskData, riskType: type },
      fieldDefinition: riskDefinitionsFields,
      riskFieldOptions,
    }),
    [riskData, type, riskDefinitionsFields, riskFieldOptions]
  );

  if (isLoading || isEmpty(defaultValues)) {
    return <MultiStepLoading />;
  }

  return (
    <DeductiblesProvider>
      <RiskContext.Provider value={riskProviderValue}>
        <MultiStepForm
          isReQuote={isReQuote}
          isEndorsement={isEndorsement}
          fullScreen={fullScreen}
          productType={type}
          fields={riskDefinitionsFields}
          defaultValues={defaultValues}
          riskDataValues={riskData}
          handleSubmit={handlePostRisk}
          handleDraftSave={handlePostDraftRisk}
        />
      </RiskContext.Provider>
    </DeductiblesProvider>
  );
};

export default AddEditQuoteBind;
